import { useState } from 'react'
import Footer from './Footer'
import '../styles/components/landing-page.css'

function LandingPage({ onGetStarted }) {
  const [showDemo, setShowDemo] = useState(false)

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Stop med at gætte – få ærlige svar
          </h1>
          <p className="hero-subtitle">
            Parkort er et værktøj der hjælper jer med at tale om de vigtige ting i jeres relation uden konflikt. 
            <strong style={{ display: 'block', marginTop: '12px', fontSize: '1.05em', color: 'var(--cyber-blue)' }}>
              💡 Sådan virker det: Vælg emner → Svar selv først → Del med modtageren → Få match score og feedback sammen
            </strong>
          </p>
          <div className="hero-ctas">
            <button 
              className="btn btn-primary btn-large" 
              onClick={onGetStarted}
              aria-label="Start med at prøve Parkort gratis - svar selv først for bedre match"
            >
              ✏️ Prøv gratis - Svar selv først →
            </button>
            <button 
              className="btn btn-secondary btn-large" 
              onClick={() => setShowDemo(!showDemo)}
              aria-label="Se demonstration af hvordan Parkort virker"
              aria-expanded={showDemo}
            >
              Se hvordan det virker
            </button>
          </div>
          <div className="trust-badges">
            <div className="badge">
              🔒 Sikker betaling
            </div>
            <a href="/privacy-policy" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="badge" style={{ cursor: 'pointer' }}>
                ✅ GDPR compliant
              </div>
            </a>
            <div className="badge">
              🛡️ Ingen data gemmes
            </div>
            
          </div>
        </div>
      </section>

      {/* What is Parkort Section */}
      <section className="what-is-section" style={{
        padding: '60px 20px',
        textAlign: 'center',
        marginBottom: '60px'
      }}>
        <h2 className="section-title">Hvad er Parkort?</h2>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          fontSize: '1.1em',
          lineHeight: '1.8',
          color: 'var(--text-secondary)'
        }}>
          <p style={{ marginBottom: '24px' }}>
            Parkort er et <strong>selvhjælpsværktøj</strong> der giver jer en struktureret måde at tale om vigtige emner på. 
            I stedet for at gætte på hvad den anden tænker, eller undgå svære samtaler, giver Parkort jer en tryg ramme.
          </p>
          <div style={{
            padding: '24px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '12px',
            border: '2px solid rgba(102, 126, 234, 0.3)',
            marginBottom: '24px',
            textAlign: 'left'
          }}>
            <h3 style={{ marginBottom: '16px', color: 'var(--cyber-blue)', fontSize: '1.2em' }}>
              📋 Sådan virker det (3 enkle skridt):
            </h3>
            <ol style={{ margin: 0, paddingLeft: '24px', lineHeight: '2' }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Vælg emner:</strong> Du vælger emner fra vores moduler (fx forventninger, konflikter, grænser)
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>✏️ Svar selv først:</strong> Du svarer på spørgsmålene først - dette giver bedre match og indsigt
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Del med modtageren:</strong> Del linket med den anden, som også svarer. I får begge match score og konkret feedback
              </li>
            </ol>
          </div>
          <p style={{ marginBottom: '24px', fontSize: '0.95em', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
            💡 <strong>Vigtigt:</strong> Jo bedre du svarer selv først, jo bedre match score og feedback får I begge. 
            Systemet sammenligner jeres svar og viser hvor I er enige og uenige.
          </p>
          <p style={{
            padding: '20px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            fontSize: '0.95em',
            fontStyle: 'italic'
          }}>
            💡 <strong>Vigtigt:</strong> Parkort er ikke en erstatning for professionel terapi eller rådgivning. 
            Det er et værktøj til selvhjælp der kan hjælpe jer med at starte samtalerne.
          </p>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="problem-solution-section">
        <h2 className="section-title">Har I også disse problemer?</h2>
        <div className="problems-grid">
          <div className="problem-card">
            <div className="problem-icon">😔</div>
            <h3>Uskrevne regler</h3>
            <p>I har forskellige forventninger, men taler ikke om dem før det bliver et problem</p>
          </div>
          <div className="problem-card">
            <div className="problem-icon">💥</div>
            <h3>Konflikter der eskalerer</h3>
            <p>Små uenigheder bliver store konflikter fordi I ikke kan tale om dem roligt</p>
          </div>
          <div className="problem-card">
            <div className="problem-icon">🤷</div>
            <h3>Gætteri og misforståelser</h3>
            <p>I gætter på hvad den anden tænker i stedet for at spørge direkte</p>
          </div>
          <div className="problem-card">
            <div className="problem-icon">😰</div>
            <h3>Undgåelse af svære emner</h3>
            <p>Vigtige samtaler bliver udskudt fordi det føles for svært at starte dem</p>
          </div>
        </div>
        
        <div className="solution-box">
          <h3>Parkort løser det sådan:</h3>
          <div className="solution-steps">
            <div className="solution-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Vælg emner</h4>
                <p>Vælg fra vores moduler hvad der betyder noget for jer</p>
              </div>
            </div>
            <div className="solution-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Del linket</h4>
                <p>Send linket til den anden – de kan svare når de er klar</p>
              </div>
            </div>
            <div className="solution-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Få svar</h4>
                <p>Se hvor I matcher og få konkret feedback til næste skridt</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2 className="section-title">Hvad får I?</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <h3>Relevante emner</h3>
            <p>Ikke generiske spørgsmål – vælg hvad der betyder noget for jer specifikt</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💬</div>
            <h3>Ærlige svar</h3>
            <p>Anonymt format giver rum til ærlighed uden konflikt</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Match score</h3>
            <p>Se visuelt hvor I matcher og hvor der er forskelle</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💡</div>
            <h3>Konkret feedback</h3>
            <p>Få handlingsanbefalinger baseret på jeres svar</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">⏰</div>
            <h3>Brug når I vil</h3>
            <p>Ingen abonnement nødvendigt – brug det når I har brug for det</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔐</div>
            <h3>Privat og sikkert</h3>
            <p>Ingen data gemmes – alt er privat mellem jer</p>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      

      {/* Pricing Section */}
      <section className="pricing-section">
        <h2 className="section-title">Vælg jeres plan</h2>
        <div className="pricing-grid">
          <div className="pricing-card free">
            <div className="pricing-header">
              <h3>Gratis</h3>
              <div className="pricing-price">0 kr</div>
            </div>
            <ul className="pricing-features">
              <li>✅ 2 gratis moduler</li>
              <li>✅ Opret ubegrænset kort</li>
              <li>✅ Få match score</li>
              <li>✅ Basis feedback</li>
            </ul>
            <button className="btn btn-outline" onClick={onGetStarted}>
              Start gratis
            </button>
          </div>
          
          <div className="pricing-card premium featured">
            <div className="featured-badge">Anbefalet</div>
            <div className="pricing-header">
              <h3>Premium</h3>
              <div className="pricing-price">19 kr/måned</div>
              <div className="pricing-savings">eller 99 kr/år (spar 57%)</div>
            </div>
            <ul className="pricing-features">
              <li>✅ Alle moduler (7+)</li>
              <li>✅ Dybere analyse</li>
              <li>✅ Detaljeret feedback</li>
              <li>✅ Gemme resultater</li>
              <li>✅ Eksport til PDF</li>
              <li>✅ Prioriteret support</li>
            </ul>
            <button 
              className="btn btn-primary" 
              onClick={onGetStarted}
              aria-label="Prøv Premium abonnement"
            >
              Prøv Premium
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="section-title">Ofte stillede spørgsmål</h2>
        <div className="faq-list">
          <FAQItem 
            question="Hvordan virker det?"
            answer="Du vælger emner fra vores moduler, deler linket med den anden, og de svarer på spørgsmålene. I får begge en match score og konkret feedback baseret på svarene."
          />
          <FAQItem 
            question="Er det sikkert?"
            answer="Ja! Vi gemmer ingen data permanent. Alt er privat mellem jer. Vi bruger sikker betaling via Stripe og overholder GDPR."
          />
          <FAQItem 
            question="Hvad koster det?"
            answer="Gratis version giver adgang til 2 moduler. Premium koster 19 kr/måned eller 99 kr/år og giver adgang til alle moduler."
          />
          <FAQItem 
            question="Kan jeg prøve først?"
            answer="Ja! Du kan oprette et kort helt gratis og se hvordan det virker. Premium moduler kan du opgradere til når du er klar."
          />
          <FAQItem 
            question="Hvad hvis vi ikke matcher?"
            answer="Det er faktisk godt! Match scoren viser hvor I matcher, men også hvor I er forskellige. Feedback'en hjælper jer med at forstå forskellene og finde løsninger."
          />
          <FAQItem 
            question="Kan jeg bruge det flere gange?"
            answer="Ja! Du kan oprette så mange kort som du vil. Brug det når I har brug for at tale om nye emner eller følg op på tidligere samtaler."
          />
          <FAQItem 
            question="Hvad hvis modtageren ikke svarer?"
            answer="Det er helt okay. Linket er gyldigt så længe du vil. Modtageren kan svare når de er klar. Du kan også sende en venlig påmindelse."
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <h2>Klar til at starte samtalen?</h2>
        <p>Opret dit første kort gratis og se hvordan det virker</p>
        <button 
          className="btn btn-primary btn-large" 
          onClick={onGetStarted}
          aria-label="Start nu med at oprette dit første kort gratis"
        >
          Start nu – det er gratis →
        </button>
      </section>
      
      <Footer />
    </div>
  )
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false)
  
  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button 
        className="faq-question" 
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${question.replace(/\s+/g, '-').toLowerCase()}`}
        aria-label={`${isOpen ? 'Luk' : 'Åbn'} spørgsmål: ${question}`}
      >
        <span>{question}</span>
        <span className="faq-icon" aria-hidden="true">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div 
          id={`faq-answer-${question.replace(/\s+/g, '-').toLowerCase()}`}
          className="faq-answer"
          role="region"
          aria-labelledby={`faq-question-${question.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <p>{answer}</p>
        </div>
      )}
    </div>
  )
}

export default LandingPage

